/** Request desk type labels (grouped under Payroll in deskTypeCatalog). */
export const PAYROLL_REIMB_OPTIN_LABEL = "Reimbursement Opt-in";
export const PAYROLL_REIMB_CLAIM_LABEL = "Reimbursement Claim";

export const PAYROLL_REIMB_DESK_LABELS = [
  PAYROLL_REIMB_OPTIN_LABEL,
  PAYROLL_REIMB_CLAIM_LABEL,
];

export const FLEXI_CLAIM_DESK_PREFIX = "flexi_claim_";
export const FLEXI_OPTIN_DESK_PREFIX = "flexi_optin_";

export function isPayrollReimbursementDeskLabel(label) {
  return PAYROLL_REIMB_DESK_LABELS.includes(label);
}

export function flexiReimbursementDeskMode(label) {
  if (label === PAYROLL_REIMB_OPTIN_LABEL) return "declare";
  if (label === PAYROLL_REIMB_CLAIM_LABEL) return "claim";
  return null;
}

export function flexiClaimIdFromDeskId(requestId) {
  const raw = String(requestId ?? "").trim();
  if (!raw) return null;
  if (/^\d+$/.test(raw)) return Number(raw);
  if (raw.startsWith(FLEXI_CLAIM_DESK_PREFIX)) {
    const id = Number(raw.slice(FLEXI_CLAIM_DESK_PREFIX.length));
    return Number.isFinite(id) && id > 0 ? id : null;
  }
  return null;
}

export function flexiOptInIdFromDeskId(requestId) {
  const raw = String(requestId ?? "").trim();
  if (!raw) return null;
  if (raw.startsWith(FLEXI_OPTIN_DESK_PREFIX)) {
    const id = Number(raw.slice(FLEXI_OPTIN_DESK_PREFIX.length));
    return Number.isFinite(id) && id > 0 ? id : null;
  }
  return null;
}

export function isFlexiClaimDeskRequest(request) {
  return (
    request?.deskMode === "flexi_claim" ||
    (request?.actionLabel === PAYROLL_REIMB_CLAIM_LABEL &&
      flexiClaimIdFromDeskId(request?.requestId) != null)
  );
}

export function isFlexiOptInDeskRequest(request) {
  return (
    request?.deskMode === "flexi_optin" ||
    (request?.actionLabel === PAYROLL_REIMB_OPTIN_LABEL &&
      flexiOptInIdFromDeskId(request?.requestId) != null)
  );
}

function mapClaimStatus(apiStatus) {
  const s = String(apiStatus || "").toUpperCase();
  if (s === "APPROVED" || s === "PARTIAL" || s === "PAID") return "Approved";
  if (s === "REJECTED" || s === "CANCELLED" || s === "REVERSED") return "Rejected";
  if (s === "DRAFT") return "Undo";
  return "Pending";
}

function mapOptInStatus(apiStatus) {
  const s = String(apiStatus || "").toUpperCase();
  if (s === "REVOKED" || s === "REJECTED") return "Rejected";
  if (s === "DRAFT") return "Undo";
  // Opt-in has no multi-step approval — submitted declarations are live.
  if (s === "SUBMITTED" || s === "APPROVED" || s === "LOCKED") return "Approved";
  return "Pending";
}

export function mapFlexiClaimToDeskItem(row, { panelKey = "review" } = {}) {
  if (!row?.id) return null;
  const submittedAtMs = row.submittedAt
    ? new Date(row.submittedAt).getTime()
    : row.createdAt
      ? new Date(row.createdAt).getTime()
      : Date.now();
  const status = mapClaimStatus(row.status);
  const pending =
    status === "Pending"
      ? row.pendingLabel || row.workflow?.pendingLabel || row.pendingRole || "Awaiting approval"
      : null;
  return {
    requestId: `${FLEXI_CLAIM_DESK_PREFIX}${row.id}`,
    flexiClaimId: row.id,
    deskMode: "flexi_claim",
    panelKey,
    action: PAYROLL_REIMB_CLAIM_LABEL,
    actionLabel: PAYROLL_REIMB_CLAIM_LABEL,
    status,
    apiStatus: row.status,
    userId: row.userId ?? null,
    userName: row.employeeName || "Employee",
    employeeCode: row.employeeCode || "",
    employeeId: row.employeeId ?? null,
    userEmail: row.userEmail || "",
    submittedAt: submittedAtMs,
    updatedAt: row.updatedAt ? new Date(row.updatedAt).getTime() : submittedAtMs,
    canAct: row.canAct === true && status === "Pending",
    canApprove: row.canAct === true && status === "Pending",
    pendingWith: pending,
    pendingLabel: pending,
    pendingRole: row.pendingRole || row.workflow?.pendingRole || null,
    workflowTrail: row.workflowApprovals || [],
    source: "api",
    formData: {
      componentCode: row.componentCode,
      claimAmount: row.claimAmount,
      claimPeriodFrom: row.claimPeriodFrom,
      claimPeriodTo: row.claimPeriodTo,
      remarks: row.remarks,
      notes: row.detailsJson?.notes || "",
    },
  };
}

export function mapFlexiOptInToDeskItem(row, { panelKey = "review" } = {}) {
  if (!row?.id) return null;
  const submittedAtMs = row.submittedAt
    ? new Date(row.submittedAt).getTime()
    : row.createdAt
      ? new Date(row.createdAt).getTime()
      : Date.now();
  const status = mapOptInStatus(row.status);
  return {
    requestId: `${FLEXI_OPTIN_DESK_PREFIX}${row.id}`,
    flexiOptInId: row.id,
    deskMode: "flexi_optin",
    panelKey,
    action: PAYROLL_REIMB_OPTIN_LABEL,
    actionLabel: PAYROLL_REIMB_OPTIN_LABEL,
    status,
    apiStatus: row.status,
    userId: row.userId ?? null,
    userName: row.employeeName || "Employee",
    employeeCode: row.employeeCode || "",
    employeeId: row.employeeId ?? null,
    userEmail: row.userEmail || "",
    submittedAt: submittedAtMs,
    updatedAt: row.updatedAt ? new Date(row.updatedAt).getTime() : submittedAtMs,
    canAct: false,
    canApprove: false,
    pendingWith: null,
    source: "api",
    formData: {
      componentCode: row.componentCode,
      declaredAmount: row.declaredAmount,
      structureAnnualMax: row.structureAnnualMax,
      financialYear: row.financialYear,
      remarks: row.remarks,
    },
  };
}

/**
 * Own requests → Request desk; actionable / others' claims → Approvals desk.
 * Opt-ins list on Request for the employee; admins see company opt-ins on Approvals (view-only).
 */
export function buildFlexiReimbursementDeskItems({
  claims = [],
  declarations = [],
  employeeId = null,
  userId = null,
} = {}) {
  const items = [];
  const selfEmp = employeeId != null ? Number(employeeId) : null;
  const selfUser = userId != null ? Number(userId) : null;

  const isOwn = (row) => {
    if (selfEmp && Number(row.employeeId) === selfEmp) return true;
    if (selfUser && Number(row.userId) === selfUser) return true;
    return false;
  };

  for (const row of claims) {
    const own = isOwn(row);
    const status = mapClaimStatus(row.status);
    const panelKey =
      !own && (status === "Pending" || row.canAct === true) ? "approvals" : own ? "review" : "approvals";
    const item = mapFlexiClaimToDeskItem(row, { panelKey });
    if (item) items.push(item);
  }

  for (const row of declarations) {
    const own = isOwn(row);
    const item = mapFlexiOptInToDeskItem(row, {
      panelKey: own ? "review" : "approvals",
    });
    if (item) items.push(item);
  }

  return items;
}
